/**
 * v1.2b — Wizard Session KV helpers
 *
 * Stores ephemeral 3-step wizard state in CARRIER_USERS KV.
 * Key pattern: app-session:<sub>:<wizardId>
 * TTL: 600 seconds (10 minutes).
 */

import type { Env } from "../types.js";

export interface WizardSession {
  step: "select-subscriber" | "select-package" | "confirm";
  subscriber_iccid?: string;
  package_template_id?: number;
  dry_run: boolean;
  initiated_at: string;
}

function sessionKey(sub: string, wizardId: string): string {
  return `app-session:${sub}:${wizardId}`;
}

// In-memory fallback for stdio mode (no KV available).
// Keys auto-expire after 600s to mirror KV TTL behavior.
const inMemorySessions = new Map<string, { session: WizardSession; expiresAt: number }>();

function pruneExpired(): void {
  const now = Date.now();
  for (const [key, val] of inMemorySessions.entries()) {
    if (val.expiresAt < now) inMemorySessions.delete(key);
  }
}

export async function loadWizardSession(
  env: Env,
  sub: string,
  wizardId: string,
): Promise<WizardSession | null> {
  const key = sessionKey(sub, wizardId);
  if (!env.CARRIER_USERS) {
    pruneExpired();
    return inMemorySessions.get(key)?.session ?? null;
  }
  const raw = await env.CARRIER_USERS.get(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as WizardSession;
  } catch {
    return null;
  }
}

export async function saveWizardSession(
  env: Env,
  sub: string,
  wizardId: string,
  session: WizardSession,
): Promise<void> {
  const key = sessionKey(sub, wizardId);
  if (!env.CARRIER_USERS) {
    inMemorySessions.set(key, { session, expiresAt: Date.now() + 600_000 });
    return;
  }
  await env.CARRIER_USERS.put(key, JSON.stringify(session), { expirationTtl: 600 });
}

export async function deleteWizardSession(
  env: Env,
  sub: string,
  wizardId: string,
): Promise<void> {
  const key = sessionKey(sub, wizardId);
  if (!env.CARRIER_USERS) {
    inMemorySessions.delete(key);
    return;
  }
  await env.CARRIER_USERS.delete(key);
}
